import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import './RememberButton.css';

/**
 * RememberButton component for discovering random memorials
 * @param {Object} props - Component props
 * @param {Array} props.memorials - Array of all memorial objects
 * @param {Object} props.viewedNotes - Map of viewed memorial IDs
 * @param {Function} props.onSelect - Callback when a memorial is selected
 * @param {boolean} props.excludeViewed - Whether to exclude already-viewed memorials
 */
const RememberButton = ({ memorials, viewedNotes = {}, onSelect, excludeViewed = false }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Get available memorials (optionally excluding viewed ones)
  const getAvailableMemorials = useCallback(() => {
    if (!excludeViewed) {
      return memorials;
    }
    return memorials.filter(m => !viewedNotes[m.id]);
  }, [memorials, viewedNotes, excludeViewed]);

  // Handle button click - select a random memorial
  const handleClick = useCallback(() => {
    const available = getAvailableMemorials();

    if (available.length === 0) {
      // All memorials have been viewed
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 3000);
      return;
    }

    // Start animation
    setIsAnimating(true);

    // Small delay for visual feedback
    setTimeout(() => {
      // Select a random memorial
      const randomIndex = Math.floor(Math.random() * available.length);
      const selectedMemorial = available[randomIndex];

      // Call the onSelect callback
      if (onSelect) {
        onSelect(selectedMemorial);
      }

      // End animation
      setIsAnimating(false);
    }, 400);
  }, [getAvailableMemorials, onSelect]);

  // Calculate remaining unviewed count
  const unviewedCount = memorials.filter(m => !viewedNotes[m.id]).length;

  return (
    <div className="remember-button-container">
      <button
        className={`remember-button ${isAnimating ? 'animating' : ''}`}
        onClick={handleClick}
        aria-label="Remember someone - open a random memorial"
        title="Remember someone"
      >
        <span className="remember-icon" aria-hidden="true">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Heart with memory symbol */}
            <path
              d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
              fill="currentColor"
            />
            {/* Sparkle/shuffle effect */}
            <circle cx="12" cy="10" r="2" fill="#f9f6f0" opacity="0.6" />
          </svg>
        </span>
        <span className="remember-text">Remember</span>
      </button>

      {/* Badge showing unviewed count */}
      {excludeViewed && unviewedCount > 0 && unviewedCount < memorials.length && (
        <span className="remember-badge" aria-label={`${unviewedCount} unviewed memorials`}>
          {unviewedCount}
        </span>
      )}

      {/* Tooltip for when all are viewed */}
      {showTooltip && (
        <div className="remember-tooltip" role="status" aria-live="polite">
          You've visited all memorials
        </div>
      )}
    </div>
  );
};

RememberButton.propTypes = {
  memorials: PropTypes.array.isRequired,
  viewedNotes: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  excludeViewed: PropTypes.bool,
};

export default RememberButton;
